import React, { useState } from "react";
import { formatDate } from "../utils/formatters";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  LinearProgress,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DnsIcon from "@mui/icons-material/Dns";
import AppsIcon from "@mui/icons-material/Apps";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import { useAppDispatch, useAppSelector, logout } from "../store";
import { MAX_ACTIVE_INSTANCES, authService } from "../services";
import { ConfirmDialog } from "../components/common";

const SettingsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { instances, activeCount } = useAppSelector((state) => state.instances);
  const { services } = useAppSelector((state) => state.services);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const totalInstances = (instances || []).length;
  const usagePercent = Math.round((activeCount / MAX_ACTIVE_INSTANCES) * 100);

  // Count unique services used
  const usedServiceIds = new Set((instances || []).map((i) => i.serviceId));

  const handleLogout = () => {
    dispatch(logout());
    window.location.href = "/login";
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) return;
    setIsDeleting(true);
    try {
      await authService.deleteAccount(user.id);
      window.location.href = "/login";
    } catch {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: { xs: "100%", sm: 800 }, mx: "auto" }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          Settings
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
          }}
        >
          Manage your account settings and preferences
        </Typography>
      </Box>
      {/* Profile Section */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Profile
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              flexDirection: { xs: "column", sm: "row" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: "primary.main",
                fontSize: "1.5rem",
                fontWeight: 700,
              }}
            >
              {user?.email?.charAt(0).toUpperCase() || "U"}
            </Avatar>
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  color: "text.primary",
                  fontSize: "1.1rem",
                }}
              >
                {user?.email || "Not logged in"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Member since{" "}
                {user?.createdAt ? formatDate(user.createdAt) : "N/A"}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* Usage Stats */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Usage
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                bgcolor: "#f0fdf4",
                borderRadius: 2,
                border: "1px solid #bbf7d0",
              }}
            >
              <DnsIcon sx={{ color: "#10b981", fontSize: 28, mb: 0.5 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#10b981",
                }}
              >
                {activeCount}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Active Instances
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                bgcolor: "#eff6ff",
                borderRadius: 2,
                border: "1px solid #bfdbfe",
              }}
            >
              <AccessTimeIcon
                sx={{ color: "#3b82f6", fontSize: 28, mb: 0.5 }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#3b82f6",
                }}
              >
                {totalInstances}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Total Instances
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                bgcolor: "#f5f3ff",
                borderRadius: 2,
                border: "1px solid #ddd6fe",
              }}
            >
              <AppsIcon sx={{ color: "#8b5cf6", fontSize: 28, mb: 0.5 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#8b5cf6",
                }}
              >
                {usedServiceIds.size}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Services Used
              </Typography>
            </Box>
          </Box>

          {/* Instance quota bar */}
          <Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "text.secondary",
                }}
              >
                Instance Quota
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                {activeCount} / {MAX_ACTIVE_INSTANCES}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={usagePercent}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "divider",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  bgcolor:
                    usagePercent >= 80
                      ? "#ef4444"
                      : usagePercent >= 60
                        ? "#f59e0b"
                        : "#10b981",
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                mt: 0.5,
                display: "block",
              }}
            >
              {MAX_ACTIVE_INSTANCES - activeCount} instance
              {MAX_ACTIVE_INSTANCES - activeCount !== 1 ? "s" : ""} remaining
            </Typography>
          </Box>
        </CardContent>
      </Card>
      {/* Account Connections */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Account Connections
          </Typography>
        </Box>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <GoogleIcon sx={{ color: "#ea4335", fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "text.secondary",
                }}
              >
                Google Account
              </Typography>
            </Box>
            {user?.googleId ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "success.main",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 16 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "success.main",
                  }}
                >
                  Connected
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Not connected
              </Typography>
            )}
          </Box>
        </Box>
      </Card>
      {/* Danger Zone */}
      <Card sx={{ border: "1px solid #fecaca" }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid #fecaca",
            bgcolor: "#fef2f2",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#dc2626",
            }}
          >
            Session
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                Sign out of your account
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                You will need to sign in again with Google
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                size="small"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Delete Account
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                size="small"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Sign Out
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Account"
        message="Are you sure? All instances will be terminated and your account will be permanently deleted."
        confirmText="Delete Account"
        confirmColor="error"
        onConfirm={handleDeleteAccount}
        onCancel={() => setDeleteDialogOpen(false)}
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default SettingsPage;
